import React from 'react';
import type { CSSProperties, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';

import { Block } from '@actual-app/components/block';
import { styles } from '@actual-app/components/styles';
import { theme } from '@actual-app/components/theme';
import { Tooltip } from '@actual-app/components/tooltip';
import { View } from '@actual-app/components/view';
import { css } from '@emotion/css';

import {
  useEnvelopeSheetName,
  useEnvelopeSheetValue,
} from '#components/budget/envelope/EnvelopeBudgetComponents';
import { FinancialText } from '#components/FinancialText';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { envelopeBudget } from '#spreadsheet/bindings';

import { TotalsList } from './TotalsList';

type ToBudgetAmountProps = {
  prevMonthName: string;
  style?: CSSProperties;
  amountStyle?: CSSProperties;
  onClick: () => void;
  onContextMenu?: MouseEventHandler;
  isTotalsListTooltipDisabled?: boolean;
};

export function ToBudgetAmount({
  prevMonthName,
  style,
  amountStyle,
  onClick,
  isTotalsListTooltipDisabled = false,
  onContextMenu,
}: ToBudgetAmountProps) {
  const { t } = useTranslation();
  const sheetName = useEnvelopeSheetName(envelopeBudget.toBudget);
  const sheetValue = useEnvelopeSheetValue({
    name: envelopeBudget.toBudget,
    value: 0,
  });
  const format = useFormat();
  const availableValue = sheetValue;
  if (typeof availableValue !== 'number' && availableValue !== null) {
    throw new Error(
      'Expected availableValue to be a number but got ' + availableValue,
    );
  }
  const num = availableValue ?? 0;
  const isNegative = num < 0;
  const isPositive = num > 0;

  return (
    <View style={{ alignItems: 'center', ...style }}>
      <View
        style={{
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2px 0 6px 0',
        }}
      >
        <Block
          style={{
            fontSize: 11,
            color: isNegative
              ? theme.toBudgetNegative
              : isPositive
                ? theme.toBudgetPositive
                : theme.pageTextSubdued,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 2,
          }}
        >
          {isNegative
            ? t('Overbudgeted')
            : isPositive
              ? t('Available to Budget')
              : t('All Funds Budgeted')}
        </Block>
        <View>
          <Tooltip
            content={
              <TotalsList
                prevMonthName={prevMonthName}
                style={{
                  padding: 7,
                }}
              />
            }
            placement="bottom"
            offset={3}
            triggerProps={{ isDisabled: isTotalsListTooltipDisabled }}
          >
            <PrivacyFilter
              style={{
                textAlign: 'center',
              }}
            >
              <Block
                onClick={onClick}
                onContextMenu={onContextMenu}
                data-cellname={sheetName}
                className={css([
                  styles.veryLargeText,
                  {
                    fontSize: 26,
                    fontWeight: 700,
                    userSelect: 'none',
                    cursor: 'pointer',
                    color: isPositive
                      ? theme.toBudgetPositive
                      : isNegative
                        ? theme.toBudgetNegative
                        : theme.toBudgetZero,
                    marginBottom: -1,
                    borderBottom: '1px solid transparent',
                    transition: 'border-color .15s ease-out',
                    ':hover': {
                      borderColor: isPositive
                        ? theme.toBudgetPositive
                        : isNegative
                          ? theme.toBudgetNegative
                          : theme.toBudgetZero,
                    },
                  },
                  amountStyle,
                ])}
              >
                <FinancialText>{format(num, 'financial')}</FinancialText>
              </Block>
            </PrivacyFilter>
          </Tooltip>
        </View>
      </View>
    </View>
  );
}
